import React, { useState } from "react";
import { HelpCircle, Loader2, Check, X, ExternalLink, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, questionModeChat } from "../lib/api";
import SavePublishBar from "./SavePublishBar";
import InlineToolChat from "./InlineToolChat";

/**
 * QuestionMode — given a topic, asks the AI to produce probing supporting +
 * challenging questions and answer each with verifiable, fact-based 2–4
 * sentence answers and source URLs. Designed to surface things the user
 * didn't even think to ask about.
 *
 * Backend: POST /api/ai/question-mode
 *   body: { topic, count? }
 *   returns: { topic, supporting: [{q,a,sources}], challenging: [{q,a,sources}], caveat }
 */
export default function QuestionMode({ initialTopic = "" }) {
  const [topic, setTopic] = useState(initialTopic);
  const [count, setCount] = useState(5);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  const reset = () => {
    setTopic("");
    setResult(null);
    setErr(null);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const t = topic.trim();
    if (!t) return;
    setLoading(true);
    setErr(null);
    setResult(null);
    try {
      const r = await api.post("/ai/question-mode", { topic: t, count });
      setResult(r.data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Question Mode failed — try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="question-mode"
      className="mt-12 border border-[#ffd60a]/30 bg-[#0a0a0a]"
    >
      <div className="px-5 py-3 border-b border-[#ffd60a]/30 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <HelpCircle size={14} className="text-[#ffd60a]" />
          <span className="font-mono text-[10px] tracking-[0.3em] text-[#ffd60a]">// QUESTION MODE</span>
        </div>
        <span className="font-mono text-[9px] tracking-[0.2em] text-zinc-500">
          AI probes both sides — supporting & challenging
        </span>
      </div>

      <div className="px-5 py-4 space-y-3" data-testid="question-mode-form">
        <p className="text-[12px] text-zinc-400 leading-relaxed">
          Drop a topic — the AI invents questions you may not have thought to ask and answers
          each one with verifiable facts. Two sets: questions that <span className="text-[#00ff88]">support</span> the
          conventional story, and questions that <span className="text-[#ff3b30]">challenge</span> it.
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            placeholder="e.g. Operation Northwoods, Bilderberg 2024, JFK Jr."
            data-testid="question-mode-topic"
            disabled={loading}
            className="flex-1 min-w-[260px] bg-[#050505] border border-white/15 focus:border-[#ffd60a] outline-none px-3 py-2 font-mono text-sm text-white placeholder:text-zinc-600"
          />
          <select
            value={count}
            onChange={(e) => setCount(parseInt(e.target.value, 10))}
            data-testid="question-mode-count"
            disabled={loading}
            title="Questions per side"
            className="bg-[#050505] border border-white/15 focus:border-[#ffd60a] outline-none px-2 py-2 font-mono text-[11px] tracking-[0.2em] uppercase text-zinc-300"
          >
            {[3, 4, 5, 6, 7, 8].map((n) => <option key={n} value={n}>{n} EACH</option>)}
          </select>
          <button
            type="button"
            onClick={submit}
            disabled={loading || !topic.trim()}
            data-testid="question-mode-submit"
            className="inline-flex items-center gap-2 bg-[#ffd60a] text-[#050505] hover:bg-white disabled:opacity-50 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.2em]"
          >
            {loading ? <><Loader2 size={12} className="animate-spin" /> PROBING…</> : <><Sparkles size={12} /> ASK</>}
          </button>
          {(result || topic || err) && (
            <button
              type="button"
              onClick={reset}
              disabled={loading}
              data-testid="question-mode-reset"
              title="Clear topic and results"
              className="inline-flex items-center gap-1.5 border border-white/15 text-zinc-400 hover:text-white hover:border-white/40 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] disabled:opacity-50"
            >
              <X size={11} /> Reset
            </button>
          )}
        </div>
      </div>

      {err && (
        <div className="px-5 pb-4 font-mono text-[11px] text-[#ff3b30]" data-testid="question-mode-error">
          ERROR — {err}
        </div>
      )}

      {result && (
        <div className="px-5 pb-5 space-y-6" data-testid="question-mode-result">
          <div className="grid lg:grid-cols-2 gap-5">
            <QASide
              label="SUPPORTING"
              icon={<Check size={12} />}
              color="#00ff88"
              items={result.supporting || []}
            />
            <QASide
              label="CHALLENGING"
              icon={<X size={12} />}
              color="#ff3b30"
              items={result.challenging || []}
            />
          </div>
          {result.caveat && (
            <div className="border-t border-white/10 pt-3 font-mono text-[10px] tracking-[0.15em] text-zinc-500 leading-relaxed">
              // {result.caveat}
            </div>
          )}
          <SavePublishBar
            kind="question_mode"
            topic={result.topic || topic}
            getPayload={() => result}
            testId="question-mode-save"
          />
          <InlineToolChat
            context={(() => {
              // Flatten the Q&A set into a plain-text block the LLM can ground itself in.
              const sup = (result.supporting || []).map((it, i) => `S${i + 1}. Q: ${it.q}\nA: ${it.a}`).join("\n\n");
              const cha = (result.challenging || []).map((it, i) => `C${i + 1}. Q: ${it.q}\nA: ${it.a}`).join("\n\n");
              return `TOPIC: ${result.topic || topic}\n\n=== SUPPORTING ===\n${sup}\n\n=== CHALLENGING ===\n${cha}`;
            })()}
            sendChat={questionModeChat}
            accent="#ffd60a"
            title="Push deeper on these questions"
            placeholder="e.g. Explain S3 in more detail, or push back on C2…"
            testIdRoot="question-mode-chat"
          />
        </div>
      )}
    </section>
  );
}

function QASide({ label, icon, color, items }) {
  if (!items.length) {
    return (
      <div className="border border-white/10 p-4 font-mono text-[10px] tracking-[0.2em] text-zinc-600">
        // NO {label} QUESTIONS RETURNED
      </div>
    );
  }
  return (
    <div data-testid={`qa-side-${label.toLowerCase()}`}>
      <div
        className="font-mono text-[10px] tracking-[0.3em] mb-3 inline-flex items-center gap-1.5 px-2 py-1 border"
        style={{ color, borderColor: `${color}80`, backgroundColor: `${color}14` }}
      >
        {icon} {label} — {items.length}
      </div>
      <ul className="space-y-3">
        {items.map((it, i) => (
          <li
            key={i}
            data-testid={`qa-card-${label.toLowerCase()}-${i}`}
            className="border border-white/10 bg-[#0F0F0F] p-4 hover:border-white/30 transition-colors"
          >
            <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-500 mb-1.5">
              Q · {String(i + 1).padStart(2, "0")}
            </div>
            <div className="text-white text-[14px] leading-snug mb-3 font-medium">
              {it.q}
            </div>
            <div className="font-mono text-[9px] tracking-[0.25em] mb-1.5" style={{ color }}>
              ANSWER
            </div>
            <p className="text-zinc-200 text-[13px] leading-relaxed whitespace-pre-wrap">{it.a}</p>
            {it.sources && it.sources.length > 0 && (
              <div className="mt-3 pt-3 border-t border-white/10">
                <div className="font-mono text-[9px] tracking-[0.25em] text-zinc-500 mb-1.5">SOURCES</div>
                <ul className="space-y-1">
                  {it.sources.map((s, j) => (
                    <li key={j}>
                      <a
                        href={s}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 font-mono text-[11px] text-[#a78bfa] hover:text-white break-all"
                      >
                        <ExternalLink size={10} className="shrink-0" /> {s}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
